// Actuals → Calendar sync (P2b, AC1): when each timekeeper's Outlook calendar was last read and what
// came of it, a button to sync the open weeks now, a re-sync of locked weeks for a past range, and a side-by-side of a person's calendar against the hours on
// record for a few weeks, which writes nothing and works before the cutover too (the AC8 parallel
// run against the Timecard Tool).

import { useState } from "react";
import { Body1, Button, Caption1, Card, Select, Subtitle2 } from "@fluentui/react-components";
import { addDays, HQ_TIME_ZONE, mondayOf, today } from "../../../core";
import { errorText, send, useApi, type CompareResponse, type PersonSyncResult, type SyncRunResponse, type SyncStatusResponse } from "../api";
import { useCodeLabels } from "../codes";
import { DateInput } from "../DateInput";
import { stamp } from "../format";
import { ErrorBar, Loading, Warnings } from "../ui";

const h = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 2 });

const RESULT: Record<PersonSyncResult["status"], string> = {
  ok: "Synced",
  denied: "Not in the Timekeepers group",
  "no-mailbox": "No mailbox",
  "no-email": "No email in the app",
  error: "Failed",
};

export function CalendarSync({ names }: { names: Map<string, string> }) {
  const status = useApi<SyncStatusResponse>("/actuals/sync");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const d = status.data;

  async function run(personId?: string, range?: { from: string; to: string; locked: "apply" | "inbox" }) {
    setBusy(range ? "range" : (personId ?? "all"));
    setError(undefined);
    setDone(undefined);
    try {
      const r = await send<SyncRunResponse>("POST", "/actuals/sync", { ...(personId ? { personId } : {}), ...(range ?? {}) });
      if (!r.window) setDone(range ? `No weeks to re-sync in that range: it must end after the cutover (${r.cutover}) and can't run past this week.` : `Nothing to sync until the cutover, ${r.cutover}.`);
      else {
        const ok = r.people.filter((p) => p.status === "ok");
        const raised = ok.reduce((t, p) => t + p.flagged, 0);
        setDone(
          `${ok.length} of ${r.people.length} synced, weeks ${r.window.from} to ${r.window.to}: ${ok.reduce((t, p) => t + p.weeks, 0)} weeks updated` +
            (raised ? `, ${raised} changes to locked weeks sent to the inbox.` : "."),
        );
      }
      status.reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(null);
  }

  const results = d?.byPerson ?? {};
  const ids = [...new Set([...names.keys(), ...Object.keys(results)])].sort((a, b) => (names.get(a) ?? a).localeCompare(names.get(b) ?? b));

  return (
    <>
      <ErrorBar message={status.error ?? error} />
      {done && <Warnings intent="info" items={[done]} />}
      <Card className="panel wide">
        <div className="toolbar">
          <Subtitle2>Calendar sync</Subtitle2>
          <Button size="small" appearance="primary" disabled={!!busy || !d?.window} onClick={() => void run()}>
            {busy === "all" ? "Syncing…" : "Sync everyone now"}
          </Button>
        </div>
        {d && (
          <Caption1 className="block">
            {d.window
              ? `Reads each timekeeper's Outlook calendar every night (09:30 UTC, before dawn in Chicago), and again Tuesday mornings before the 12:00 cutoff. It reads only weeks still open: now, ${d.window.from === d.window.to ? `the week of ${d.window.from}` : `the weeks of ${d.window.from} and ${d.window.to}`}. Locked weeks are left alone unless you re-sync them below.`
              : `The sync starts on the cutover, ${d.cutover} (Settings). Until then, compare a person's calendar with the Timecard Tool hours below.`}
            {d.lastRun && ` Last full run ${stamp(d.lastRun.at, HQ_TIME_ZONE)} Central, by ${d.lastRun.by}.`}
          </Caption1>
        )}
        {status.loading ? (
          <Loading />
        ) : (
          <div className="table-scroll">
            <table className="edit-table compact">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Last synced (Central)</th>
                  <th>Result</th>
                  <th className="num">Weeks updated</th>
                  <th className="num">Added</th>
                  <th className="num">Changed</th>
                  <th className="num">Removed</th>
                  <th className="num">To the inbox</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ids.map((id) => {
                  const r = results[id];
                  return (
                    <tr key={id}>
                      <td>{names.get(id) ?? r?.name ?? id}</td>
                      <td>{r ? stamp(r.at, HQ_TIME_ZONE) : <span className="muted">Not yet</span>}</td>
                      <td>{r ? `${RESULT[r.status]}${r.code ? ` (Graph ${r.code})` : ""}` : ""}</td>
                      <td className="num">{r?.status === "ok" ? r.weeks : ""}</td>
                      <td className="num">{r?.status === "ok" ? r.added : ""}</td>
                      <td className="num">{r?.status === "ok" ? r.updated : ""}</td>
                      <td className="num">{r?.status === "ok" ? r.removed : ""}</td>
                      <td className="num">{r?.status === "ok" ? r.flagged : ""}</td>
                      <td>
                        <Button size="small" appearance="subtle" disabled={!!busy || !d?.window} onClick={() => void run(id)}>
                          {busy === id ? "Syncing…" : "Resync"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Caption1 className="muted">
              "Not in the Timekeepers group": Exchange keeps the app out of that mailbox. Add the person to Timekeepers if their hours should count.
            </Caption1>
          </div>
        )}
      </Card>
      <Resync names={names} busy={busy} onRun={(personId, range) => void run(personId || undefined, range)} />
      <Reconcile />
      <Compare names={names} />
    </>
  );
}

/**
 * Re-sync locked weeks for a past range (Chris, 2026-10-14): admins only. Taking Outlook's version
 * rewrites each locked week from the calendar, keeping recodes and moved blocks, and logs it on the
 * week as an administrative change. Sending differences to the inbox changes nothing until reviewed.
 */
function Resync({ names, busy, onRun }: { names: Map<string, string>; busy: string | null; onRun: (personId: string, range: { from: string; to: string; locked: "apply" | "inbox" }) => void }) {
  const people = [...names.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const [personId, setPersonId] = useState("");
  const [from, setFrom] = useState(addDays(mondayOf(today()), -14));
  const [to, setTo] = useState(addDays(mondayOf(today()), -1));
  const [locked, setLocked] = useState<"apply" | "inbox">("apply");
  return (
    <Card className="panel wide">
      <div className="toolbar">
        <Subtitle2>Re-sync locked weeks</Subtitle2>
        <Select size="small" value={personId} onChange={(_, x) => setPersonId(x.value)} aria-label="Who">
          <option value="">Everyone</option>
          {people.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
        <label className="inline-field">
          <Caption1>From</Caption1>
          <DateInput size="small" type="date" value={from} onChange={(_, x) => x.value && setFrom(x.value)} aria-label="Re-sync from" />
        </label>
        <label className="inline-field">
          <Caption1>to</Caption1>
          <DateInput size="small" type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} aria-label="Re-sync to" />
        </label>
        <Select size="small" value={locked} onChange={(_, x) => setLocked(x.value as "apply" | "inbox")} aria-label="How to treat locked weeks">
          <option value="apply">Take Outlook's version</option>
          <option value="inbox">Send differences to the inbox</option>
        </Select>
        <Button size="small" disabled={!!busy || to < from} onClick={() => onRun(personId, { from, to, locked })}>
          {busy === "range" ? "Re-syncing…" : "Re-sync these weeks"}
        </Button>
      </div>
      <Caption1 className="block">
        Whole weeks, Monday to Sunday, from the cutover on; up to 26 weeks. <strong>Take Outlook's version</strong> rewrites each locked week from the
        calendar as it is now, keeping recodes and moved blocks, and logs the re-sync on the week (it shows in Changes). Outlook rewrites the past
        when a recurring series or a category is deleted, so check with Compare first. <strong>Send differences to the inbox</strong> changes nothing
        until each difference is reviewed.
      </Caption1>
    </Card>
  );
}

/** A person's calendar against the hours on record, week by week. Reads only; writes nothing. */
function Compare({ names }: { names: Map<string, string> }) {
  const { label } = useCodeLabels();
  const people = [...names.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const [personId, setPersonId] = useState("");
  const [from, setFrom] = useState(addDays(mondayOf(today()), -21));
  const [to, setTo] = useState(addDays(mondayOf(today()), -7));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<CompareResponse>();
  const [open, setOpen] = useState<string | null>(null);
  const who = personId || people[0]?.[0] || "";

  async function compare() {
    setBusy(true);
    setError(undefined);
    try {
      setResult(await send<CompareResponse>("POST", "/actuals/sync/compare", { personId: who, from, to }));
      setOpen(null);
    } catch (err) {
      setError(errorText(err));
      setResult(undefined);
    }
    setBusy(false);
  }

  return (
    <Card className="panel wide">
      <div className="toolbar">
        <Subtitle2>Compare with the calendar</Subtitle2>
        <Select size="small" value={who} onChange={(_, x) => setPersonId(x.value)} aria-label="Person">
          {people.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
        <label className="inline-field">
          <Caption1>Weeks from</Caption1>
          <DateInput size="small" type="date" value={from} onChange={(_, x) => x.value && setFrom(x.value)} aria-label="From" />
        </label>
        <label className="inline-field">
          <Caption1>to</Caption1>
          <DateInput size="small" type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} aria-label="To" />
        </label>
        <Button size="small" disabled={busy || !who} onClick={() => void compare()}>
          {busy ? "Reading…" : "Compare"}
        </Button>
      </div>
      <Caption1 className="block">
        Reads the person's Outlook calendar now and counts it with the same rules, beside the hours on record (from the Timecard Tool import or the sync).
        Nothing is saved. Up to 14 weeks at a time.
      </Caption1>
      <ErrorBar message={error} />
      {result && (
        <table className="edit-table compact">
          <thead>
            <tr>
              <th>Week of</th>
              <th className="num">On record</th>
              <th className="num">Outlook now</th>
              <th className="num">Difference</th>
              <th>Codes that differ</th>
            </tr>
          </thead>
          <tbody>
            {result.weeks.map((w) => {
              const codes = [...new Set([...Object.keys(w.recorded.byCode), ...Object.keys(w.calendar.byCode)])];
              const differ = codes
                .map((c) => ({ c, rec: w.recorded.byCode[c] ?? 0, cal: w.calendar.byCode[c] ?? 0 }))
                .filter((x) => Math.abs(x.rec - x.cal) >= 0.01)
                .sort((a, b) => Math.abs(b.cal - b.rec) - Math.abs(a.cal - a.rec));
              const diff = w.calendar.hours - w.recorded.hours;
              return (
                <tr key={w.monday} className={differ.length ? "clickable" : undefined} onClick={() => differ.length && setOpen(open === w.monday ? null : w.monday)}>
                  <td>{w.monday}</td>
                  <td className="num">{h(w.recorded.hours)}</td>
                  <td className="num">{h(w.calendar.hours)}</td>
                  <td className="num">{Math.abs(diff) < 0.01 ? "—" : `${diff > 0 ? "+" : "−"}${h(Math.abs(diff))}`}</td>
                  <td>
                    {!differ.length ? (
                      <span className="muted">Same</span>
                    ) : open === w.monday ? (
                      differ.map((x) => (
                        <Caption1 key={x.c} className="block">
                          {x.c ? label(x.c) : "No code"}: {h(x.rec)} on record, {h(x.cal)} in Outlook
                        </Caption1>
                      ))
                    ) : (
                      <button type="button" className="link-button">
                        {differ.length} {differ.length === 1 ? "code" : "codes"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {result && !result.weeks.length && <Body1>No weeks in that range.</Body1>}
    </Card>
  );
}

/**
 * The parallel run (AC8): a month's Timecard Tool hours beside what each person's Outlook calendar gives
 * now, by person and code. Reads only; writes nothing.
 */
function Reconcile() {
  const { label } = useCodeLabels();
  const last = new Date();
  last.setUTCDate(1);
  last.setUTCMonth(last.getUTCMonth() - 1);
  const [month, setMonth] = useState(last.toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [open, setOpen] = useState<string | null>(null);
  const [result, setResult] = useState<{ month: string; people: { personId: string; name: string; status: string; recorded: number; calendar: number | null; byCode: { code: string; recorded: number; calendar: number }[] }[] }>();
  async function run() {
    setBusy(true);
    setError(undefined);
    try {
      setResult(await send("POST", "/actuals/sync/reconcile", { month }));
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }
  const total = (f: (p: NonNullable<typeof result>["people"][number]) => number) => (result?.people ?? []).reduce((t, p) => t + f(p), 0);
  return (
    <Card className="panel wide">
      <div className="toolbar">
        <Subtitle2>Reconcile a month</Subtitle2>
        <DateInput size="small" type="month" value={month} onChange={(_, x) => x.value && setMonth(x.value)} aria-label="Month" />
        <Button size="small" disabled={busy} onClick={() => void run()}>
          {busy ? "Reading calendars…" : "Reconcile"}
        </Button>
      </div>
      <Caption1 className="block">
        The parallel run: the month's hours imported from the Timecard Tool beside what each person's Outlook calendar gives now under the same rules.
        Differences the counting rules explain (two-code splits, all-day events, Free time) are expected; anything else is worth a look before the
        Timecard Tool is retired. Nothing is saved.
      </Caption1>
      <ErrorBar message={error} />
      {result && (
        <table className="edit-table compact">
          <thead>
            <tr>
              <th>Person</th>
              <th className="num">Timecard Tool</th>
              <th className="num">Outlook now</th>
              <th className="num">Difference</th>
              <th>Codes that differ</th>
            </tr>
          </thead>
          <tbody>
            {result.people.map((p) => {
              const diff = p.calendar === null ? null : p.calendar - p.recorded;
              return (
                <tr key={p.personId} className={p.byCode.length ? "clickable" : undefined} onClick={() => p.byCode.length && setOpen(open === p.personId ? null : p.personId)}>
                  <td>{p.name}</td>
                  <td className="num">{h(p.recorded)}</td>
                  <td className="num">{p.calendar === null ? (p.status === "denied" ? "not a timekeeper" : p.status) : h(p.calendar)}</td>
                  <td className="num">{diff === null ? "" : Math.abs(diff) < 0.01 ? "—" : `${diff > 0 ? "+" : "−"}${h(Math.abs(diff))}`}</td>
                  <td>
                    {!p.byCode.length ? (
                      p.calendar === null ? "" : <span className="muted">Same</span>
                    ) : open === p.personId ? (
                      p.byCode.map((x) => (
                        <Caption1 key={x.code} className="block">
                          {x.code ? label(x.code) : "No code"}: {h(x.recorded)} in the Timecard Tool, {h(x.calendar)} in Outlook
                        </Caption1>
                      ))
                    ) : (
                      <button type="button" className="link-button">
                        {p.byCode.length} {p.byCode.length === 1 ? "code" : "codes"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td>
                <strong>Everyone</strong>
              </td>
              <td className="num">
                <strong>{h(total((p) => p.recorded))}</strong>
              </td>
              <td className="num">
                <strong>{h(total((p) => p.calendar ?? 0))}</strong>
              </td>
              <td />
              <td />
            </tr>
          </tbody>
        </table>
      )}
    </Card>
  );
}
